import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import PersonTagsManager from 'managers/tags/person';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { Tag } from 'models/tag';
import { TopicThread } from 'models/topicThread';
import './ownerTags.html';
import './ownerTags.scss';

components.preload('tags-list');

const hideDelay = 100

export type CommentOwnerTagsVMAlignment = 'left' | 'center';

export interface CommentOwnerTagsVMConfig {
    comment?: SubscribableOrNullableValue<TopicThread>
    anchor?: SubscribableOrNullableValue<HTMLElement>
    inline?: SubscribableOrNullableValue<boolean>
    align?: SubscribableOrNullableValue<CommentOwnerTagsVMAlignment>
}

@injectable()
export default class CommentOwnerTagsVM {
    labels = labels

    private hideTimeout: NodeJS.Timeout | undefined

    tags: Subscribable<Array<Tag>>
    
    isActive: Subscribable<boolean>
    isLoadingTags: Subscribable<boolean>
    hasTags: Subscribable<boolean>
    isShown: Subscribable<boolean>
    isHovered = ko.observable(false)    

    constructor(
        @inject(wccModules.componentConfig) config: CommentOwnerTagsVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const anchor = ko.flattenComputed(config.anchor);
        const inlineMode = ko.flattenComputed(config.inline, false);
        const align = ko.flattenComputed(config.align, 'center');

        const comment = ko.flattenComputed(config.comment);
        const postedById = comment.pluck(c => c.postedById);

        const currentUserManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        const user = currentUserManager.pluck(m => m.person);
        const isModeratorOrObserver = user.pluck(u => u.isModeratorOrObserver, false);

        const participantTagsManager = storage.get(PersonTagsManager, [postedById]);
        this.isLoadingTags = participantTagsManager.pluck(m => m.loading, true).extend({ deferred: true });
        this.tags = participantTagsManager.pluck(m => m.tags, []).filter(tag => !tag.isHidden());
        this.hasTags = this.tags.is(tags => tags.length > 0);

        const isAllowed = ko.pureComputed(() => isModeratorOrObserver());
        const isRequired = this.isHovered.toFlag();

        this.isShown = ko.pureComputed(() => inlineMode() || this.isHovered());
        this.isActive = ko.pureComputed(() => isAllowed() && (inlineMode() || isRequired()));

        effects.register((isAllowed, anchor) => {
            if (isAllowed && anchor != undefined) {
                return [
                    DOMHelpers.onNodeEvent(anchor, 'mouseenter', () => this.onMouseEnter()),
                    DOMHelpers.onNodeEvent(anchor, 'mouseleave', () => this.onMouseLeave()),

                    withEffect(inlineMode => {
                        if (inlineMode)
                            return this.tags.subscribe(_.noop);
                    }, [inlineMode])
                ]
            }
        }, [isAllowed, anchor]);

        effects.register((inlineMode) => {
            if (!inlineMode)
                return [
                    DOMHelpers.onNodeEvent(<HTMLElement>info.element, 'mouseenter', () => this.onMouseEnter(true)),
                    DOMHelpers.onNodeEvent(<HTMLElement>info.element, 'mouseleave', () => this.onMouseLeave()),
                ]
        }, [inlineMode]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.isActive,

            'css': {
                'topic__comment__owner-tags': true,
                'topic__comment__owner-tags--inline': inlineMode
            }
        }, this);

        ko.applyBindingsToNode(info.element, {
            'css': align.mapSingle(align => `topic__comment__owner-tags--align-${align}`)
        });
    }

    private onMouseEnter(keepAliveOnly = false) {
        if (this.isHovered() || !keepAliveOnly) {
            this.isHovered(true);

            if (this.hideTimeout != undefined)
                clearTimeout(this.hideTimeout);
        }
    }

    private onMouseLeave() {
        if (this.hideTimeout != undefined)
            clearTimeout(this.hideTimeout);

        this.hideTimeout = setTimeout(() => this.isHovered(false), hideDelay);
    }
}